// Auto-cura da cota da ORIGEM: apagar e recriar o `iptv-catalog` quando o
// disjuntor de gravação do catálogo está em `off`.
//
// ── O que o aparelho mostrou (LG do dono, 2.39.0) ──
// Disjuntor em `off` desde o primeiro boot, `iptv-catalog` v3 com ZERO registros
// nos 7 stores, e mesmo assim o IndexedDB da origem recusava tudo: um play de
// 16 s só gravava no fallback do localStorage, os 96 tombstones do outbox davam
// "enqueue falhou", `QuotaExceededError`. Uma ação só, `deleteDatabase
// ('iptv-catalog')` com o app aberto (success em 558 ms, sem `blocked`), e no
// boot seguinte: o outbox drenou os 96 em 49 s, a posição voltou pro IndexedDB
// (e a cópia do fallback saiu), zero `QuotaExceededError`. Controle: o boot
// imediatamente anterior também veio de fechar e abrir, e ainda tinha os 96
// "enqueue falhou" — a virada acompanhou o delete, não o relançamento.
//
// Leitura: o `iptv-catalog`, mesmo sem registros lógicos, retinha o espaço das
// tentativas antigas de gravar o catálogo inteiro (lixo físico do LevelDB, ou
// espaço alocado). Apagar o BANCO devolve a cota à origem inteira: favoritos,
// progresso e outbox junto.
//
// ── A regra ──
//  • SÓ em `off`. Em `off`, o catálogo DO PROVEDOR QUE CAIU é vazio ou ilegível
//    por construção: o aparelho só chega lá por um `set()` recusado, e o `set()`
//    apaga meta e surfaces daquele provedor ANTES de gravar.
//    ⚠️ Mas o banco é de TODOS os provedores. Numa conta com vários, ele pode
//    guardar a prioridade de OUTRO provedor (gravada antes, em `priority-only`), e
//    ela sai junto (review do `40c93bdd`, P2). O custo é pequeno e declarado: em
//    `off` ela nunca seria regravada, o leitor quente a aposentaria no TTL (24 h)
//    de qualquer jeito, e aquele provedor volta a vir da rede, como vem todo
//    catálogo em `off`. Apagar só os registros do provedor que caiu não serve: no
//    aparelho o banco já tinha ZERO registros e ainda retinha a cota — ela só
//    voltou com o BANCO apagado.
//  • NÃO em `priority-only`: ali o banco guarda `liveChannels`, o caminho do
//    vídeo, e o aparelho PROVOU que grava a prioridade. Se a cota passar a recusar
//    a própria prioridade, o disjuntor cai pra `off` (noteWriteResult) — e aí
//    este reset vale. Apagar em `priority-only` custaria um download a mais e o
//    cache do vídeo, pra um aparelho que não está no buraco de cota zero.
//  • UMA vez por EPISÓDIO de `off` (o teto). O episódio começa quando o modo vira
//    `off` (`modoDesde`, persistido junto do modo) e só termina se o modo SOBE,
//    o que o automático nunca faz: só o "Atualizar catálogo" que chega ao `set()`
//    e grava a prioridade, ou o "Limpar dados". Então, COM O `localStorage`
//    GRAVANDO, o número de resets é limitado por ações do usuário, não por boots.
//    Se a cota continuar zero depois do reset, o disjuntor segue `off`, nada tenta
//    gravar o catálogo, e o boot seguinte vê "já feito neste episódio".
//    ⚠️ Com o `localStorage` RECUSANDO escrita, isso NÃO vale (review do
//    `40c93bdd`, P2; sonda: 3 boots, 3 resets). Nem o modo do disjuntor nem este
//    registro sobrevivem ao boot: cada boot recomeça em `full`, tenta gravar, cai
//    em `off` e apaga — UM reset por boot. É o mesmo teto do próprio disjuntor
//    nesse caso (uma tentativa de gravação por boot), e o reset segue a tentativa
//    que falhou: não soma escrita, só devolve o que ela deixou.
//  • Registro ANTES do delete (write-ahead): se o delete travar ou o app morrer
//    no meio, o boot seguinte não repete. Um delete que falha gasta o reset do
//    episódio — e a telemetria diz.
//  • Com PRAZO (3 s, o mesmo do "Limpar dados"): delete bloqueado por conexão de
//    outro documento (web com várias abas) não segura o boot. O `onversionchange`
//    do idb-opener já faz os outros documentos soltarem; no aparelho, 558 ms com
//    o app aberto.
//
// ── Onde roda ──
//  • No BOOT (bootstrap, passo 5.7), AGUARDADO, antes do outbox abrir (passo 8) e
//    antes do render: os escritores já pegam a cota de volta no mesmo boot. É o
//    que cura quem JÁ está em `off` (o aparelho do dono está desde o 1º boot).
//  • Na TRANSIÇÃO pra `off` durante a sessão (catalog-snapshot, depois do
//    disjuntor aprender com um `set()` recusado): sem esperar o próximo boot.
//
// Limite declarado: sem `localStorage` (recusando escrita), o registro vale só
// na sessão — como o próprio disjuntor, que nesse caso recomeça em `full` no
// boot seguinte. Resultado: um reset por boot (ver o teto, acima).
//
// COMPAT Chr38: só JSON, try/catch, Date.now.

import { getCapability } from '@app-iptv/device'

import { CATALOG_DB_NAME } from './catalog-store'
import { catalogWriteMode, modoDesde } from './catalog-write-breaker'
import { destroyDbsByName } from './idb-opener'
import { track } from './qoe'

/** Prazo do delete. O mesmo teto do "Limpar dados" (idb-opener,
 *  DESTROY_ALL_TIMEOUT_MS): no aparelho o delete levou 558 ms COM o app aberto. */
export const AUTO_CURA_TIMEOUT_MS = 3_000

const RESET_KEY = 'swipe:catalog-db-reset:v1'

/** Piso da sessão quando o `localStorage` recusa escrita. */
let sessionResetEm: number | null = null

function lerResetEm(): number | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(RESET_KEY)
    if (!raw) return null
    const v: unknown = JSON.parse(raw)
    const em = v && typeof v === 'object' ? (v as { em?: unknown }).em : null
    return typeof em === 'number' && isFinite(em) ? em : null
  } catch {
    return null
  }
}

function gravarResetEm(em: number): void {
  sessionResetEm = em
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(RESET_KEY, JSON.stringify({ em }))
  } catch {
    // Piso: vale na sessão (ver "Limite declarado").
  }
}

export type ResultadoAutoCura = 'nao-precisa' | 'ja-feito' | 'ok' | 'timeout' | 'erro'

/**
 * Apaga o `iptv-catalog` se o disjuntor está em `off` e este episódio ainda não
 * teve o seu reset. Nunca lança. O banco é recriado vazio na próxima abertura.
 */
export async function curarCotaSePreciso(
  motivo: 'boot' | 'transicao',
  opts: { timeoutMs?: number } = {},
): Promise<ResultadoAutoCura> {
  if (catalogWriteMode() !== 'off') return 'nao-precisa'
  const desde = modoDesde()
  const ultimo = sessionResetEm !== null ? sessionResetEm : lerResetEm()
  // Episódio desconhecido (`off` gravado sem o início): qualquer reset já feito
  // conta — erra pro lado de não repetir.
  if (ultimo !== null && (desde === null || ultimo >= desde)) return 'ja-feito'

  gravarResetEm(Date.now())
  let resultado: 'ok' | 'timeout' | 'erro'
  try {
    const r = await destroyDbsByName([CATALOG_DB_NAME], { timeoutMs: opts.timeoutMs ?? AUTO_CURA_TIMEOUT_MS })
    resultado = r.timedOut ? 'timeout' : r.failed.length > 0 ? 'erro' : 'ok'
  } catch {
    resultado = 'erro'
  }
  track({
    type: 'catalog_db_reset',
    ts: Math.floor(Date.now() / 1000),
    motivo,
    resultado,
    tier: getCapability('cpuTier'),
  })
  return resultado
}

/** Só pra teste: zera a memória da sessão (o persistido o teste controla). */
export function __resetAutoCuraForTest(): void {
  sessionResetEm = null
}
